import React, { useEffect, useMemo, useRef } from "react"

import { Spin } from "antd"
import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip
} from "chart.js"
import { Line } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { debounce } from "../../../Utils/utils"
import { getChartOptions } from "./chartsOptions"
import { getGradient, tooltipLine } from "./chartsPlagins"

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

ChartJS.defaults.color = "#667085"
// ChartJS.defaults.font.family = 'FontAwesome, Inter, sans-serif';

ChartJS.defaults.plugins.tooltip.titleAlign = "center"
ChartJS.defaults.plugins.tooltip.padding = "12"
ChartJS.defaults.plugins.tooltip.titleMarginBottom = 8
ChartJS.defaults.plugins.tooltip.titleFont = {
  fontFamily: "Inter, sans-serif",
  lineHeight: 1.2,
  size: 14,
  style: "normal",
  weight: "700"
}

let testDataAlertsDescriptorEmpty = {
  labels: [
    "2023-04-01",
    "2023-04-02",
    "2023-04-03",
    "2023-04-04",
    "2023-04-05",
    "2023-04-06",
    "2023-04-07",
    "2023-04-08",
    "2023-04-09",
    "2023-04-10",
    "2023-04-11",
    "2023-04-12",
    "2023-04-13",
    "2023-04-14",
    "2023-04-15"
  ],
  result: {
    data: null,
    descriptors: null
  },
  total_count: 0,
  total_volume: 0
}

// Sized so the chart fills the fixed-height card it sits in, the same as Alerts by Country.
const CHART_HEIGHT = 506

const AlertsDescriptorChart = () => {
  const { t } = useTranslation()

  // let alertsDescriptorData = testDataAlertsDescriptor;
  // alertsDescriptorData = null;
  // let alertsDescriptorFetching = false;
  const { alertsByDescriptorData, alertsByDescriptorFetching } = useSelector(
    (state) => state.dashboard
  )
  const theme = useSelector((state) => state.nav.theme)

  //for better resize chart if resize window
  const chartRef = useRef(null)

  const handleResize = debounce(() => {
    if (chartRef.current) {
      chartRef.current.resize()
    }
  }, 500)

  //for udate chart after changing browser tab if chart is unvisible
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && chartRef.current) {
      chartRef.current.update()
    }
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      window.removeEventListener("resize", handleResize)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  const chartOptions = useMemo(() => getChartOptions(theme, "descriptors"), [theme])

  const getChartData = (data) => ({
    labels: data.labels,
    datasets: [
      {
        label: "test1",
        data: data?.result?.data,
        descriptors: data?.result?.descriptors,
        borderColor: "#225AEA",
        borderWidth: 2,
        pointHoverRadius: 8,
        pointHoverBorderWidth: 2,
        pointHoverBackgroundColor: "white",
        yAxisID: "y",
        fill: true,
        backgroundColor: function (context) {
          const chart = context.chart
          const { ctx, chartArea } = chart
          if (!chartArea) return
          return getGradient(ctx, chartArea)
        },
        ...(data.labels.length === 1
          ? { pointHoverRadius: 6, pointRadius: 4 }
          : { pointHoverRadius: 6, pointRadius: 0 })
      }
    ]
  })

  if (!alertsByDescriptorData) {
    const chartData = getChartData(testDataAlertsDescriptorEmpty)
    return (
      <Spin spinning={alertsByDescriptorFetching}>
        <div className="card-empty-state-wrapper">
          <Line ref={chartRef} options={chartOptions} data={chartData} height={CHART_HEIGHT} />
          <span className="empty-state-title">{t("No data available")}</span>
        </div>
      </Spin>
    )
  }

  const chartData = getChartData(alertsByDescriptorData)

  return (
    <Spin spinning={alertsByDescriptorFetching}>
      <Line
        ref={chartRef}
        options={chartOptions}
        data={chartData}
        height={CHART_HEIGHT}
        plugins={[tooltipLine]}
      />
    </Spin>
  )
}

export default AlertsDescriptorChart
